/** Connected blocks (owner feedback F21): widgets that talk to the platform itself: time, tasks, people, files, meetings, signatures, the Copilot. */
import { L, P, SIZE_LABELS, defineBlock } from './custom-blocks';
import { PEOPLE, POSTS, WORK } from './custom-blocks-kit';

const RECORDS = ['records', 'static'] as const;
const row = (en: string, de: string, def: string, expect: 'text' | 'number' | 'date' | 'any' | 'boolean' = 'text') => P.expr(en, de, { scope: 'row', expect, def });

export const platformBlocks = {
  nativedetail: defineBlock({
    name: L('Native detail panel', 'Standard-Detailansicht'),
    desc: L(
      'Standard task, record, activity, meeting, person and Inbox details with their normal fields and actions.',
      'Standardansichten für Aufgaben, Datensätze, Aktivitäten, Termine, Personen und Posteingangseinträge mit ihren Feldern und Aktionen.',
    ),
    family: 'platform',
    icon: 'task',
    keywords: ['task detail', 'native UI', 'standard task', 'record detail', 'activity', 'inbox', 'drawer'],
    summary:
      'Embeds the real platform detail UI. kind task uses the task type layout and normal Inbox actions; record uses the record interface; engagement uses the activity card; meeting uses the booking detail with rescheduling/cancellation; member uses the person card; inbox uses its specialized detail (approval, signature, notification or task). Put it inside a drawer/modal or any container. id is an expression returning the item id; inbox expects a full Inbox id such as task:<uuid>. object is required only for record. All APIs enforce the viewing member permissions. Design and preview modes never run native actions.',
    props: {
      kind: P.pick('Detail of', 'Details von', ['task', 'record', 'engagement', 'meeting', 'member', 'inbox'] as const, 'task', {
        labels: {
          task: L('Task', 'Aufgabe'),
          record: L('Record', 'Datensatz'),
          engagement: L('Activity', 'Aktivität'),
          meeting: L('Meeting', 'Termin'),
          member: L('Person', 'Person'),
          inbox: L('Inbox item', 'Posteingangseintrag'),
        },
      }),
      id: P.expr('Item ID', 'Eintrags-ID', {
        expect: 'text',
        def: '',
        hint: L(
          'For example state.selected_task or row.id.',
          'Zum Beispiel state.selected_task oder row.id.',
        ),
      }),
      object: P.object('Record object', 'Datensatzobjekt', { opt: true }),
    },
    example: {
      props: { kind: 'task', id: 'state.selected_task' },
      state: [{ key: 'selected_task', type: { kind: 'text' }, initial: '' }],
    },
  }),

  component: defineBlock({
    name: L('Reusable component', 'Wiederverwendbare Komponente'),
    desc: L(
      'Place a saved component inside this component and supply its inputs.',
      'Eine gespeicherte Komponente in diese Komponente einfügen und ihre Eingaben übergeben.',
    ),
    family: 'platform',
    icon: 'box',
    keywords: ['nested component', 'reuse', 'compose', 'embed', 'child component'],
    summary:
      'Embeds a saved component by key or UUID. Use component.list/get to find its key and input props. inputs maps prop names to expressions in the parent scope (state, props, row, data). Each instance has isolated local state and uses the child published definition and its own permission-checked data/actions. Circular references and nesting deeper than eight components are blocked. Put it in any container, drawer, modal or repeated row.',
    props: {
      component: P.str('Component key or ID', 'Komponentenschlüssel oder ID', { def: '', max: 80 }),
      inputs: P.items(
        'Inputs',
        'Eingaben',
        {
          key: P.str('Input name', 'Eingabename', { def: '', max: 40 }),
          value: P.expr('Value', 'Wert', { expect: 'any', def: '' }),
        },
        { titleKey: 'key', max: 30, def: [] },
      ),
    },
    example: { props: { component: '', inputs: [] } },
  }),

  stopwatch: defineBlock({
    name: L('Stopwatch', 'Stoppuhr'),
    desc: L('Start, pause and reset a timer in the browser, with laps; it can hand the time to an action.', 'Einen Zeitmesser im Browser starten, anhalten und zurücksetzen, mit Runden; die Zeit kann an eine Aktion übergeben werden.'),
    family: 'platform',
    icon: 'stopwatch',
    keywords: ['timer', 'lap', 'time tracking', 'elapsed', 'stop watch'],
    summary: 'A client-side stopwatch. On stop it calls onStop with event.seconds; bind keeps the last elapsed seconds in a number state. For a timer that survives a reload use the timer block with records.',
    props: {
      label: P.tpl('Label', 'Bezeichnung', { opt: true }),
      bind: P.state('Elapsed seconds are kept in', 'Vergangene Sekunden werden gespeichert in', ['number'], { opt: true }),
      laps: P.bool('Laps', 'Runden', true),
      onStop: P.call('When stopped', 'Beim Anhalten', { extras: { seconds: 'number' } }),
      size: P.pick('Size', 'Größe', ['md', 'lg'] as const, 'lg', { labels: { md: SIZE_LABELS.md!, lg: SIZE_LABELS.lg! } }),
    },
    example: { props: { label: L('Call duration', 'Gesprächsdauer'), laps: true } },
  }),

  countdown: defineBlock({
    name: L('Time left', 'Restzeit'),
    desc: L('Days, hours, minutes and seconds until a date, with an action when it is reached.', 'Tage, Stunden, Minuten und Sekunden bis zu einem Datum, mit einer Aktion, wenn es erreicht ist.'),
    family: 'platform',
    icon: 'hourglass',
    keywords: ['deadline', 'due', 'time left', 'launch', 'remaining'],
    summary: 'Counts down to until (a date or date-time expression). onDone runs once when it has passed.',
    props: {
      until: P.expr('Until', 'Bis', { expect: 'any', def: 'addDays(today(), 7)' }),
      label: P.tpl('Label', 'Bezeichnung', { opt: true }),
      doneText: P.tpl('When it has passed', 'Wenn es vorbei ist', { def: 'It has started' }),
      onDone: P.call('When it is reached', 'Wenn es erreicht ist'),
    },
    example: { props: { label: L('Until the launch', 'Bis zum Start'), until: 'addDays(today(), 12)' } },
  }),

  checklist: defineBlock({
    name: L('Checklist', 'Checkliste'),
    desc: L('Items with checkboxes and a progress bar; ticking one can update a record.', 'Einträge mit Kontrollkästchen und Fortschrittsbalken; Abhaken kann einen Datensatz aktualisieren.'),
    family: 'platform',
    icon: 'task',
    keywords: ['todo', 'tasks', 'subtasks', 'tick', 'onboarding steps', 'to do list'],
    summary: 'A checklist over rows: label and done are expressions of a row; ticking calls onToggle (event.checked, row), usually a record.update.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Data', 'Daten', RECORDS),
      title: P.tpl('Title', 'Titel', { opt: true }),
      label: row('Text of an item', 'Text eines Eintrags', 'row.title'),
      done: row('Ticked when', 'Abgehakt, wenn', 'row.done', 'boolean'),
      progress: P.bool('Progress bar', 'Fortschrittsbalken', true),
      onToggle: P.call('When an item is ticked', 'Wenn ein Eintrag abgehakt wird', { scope: 'row', extras: { checked: 'boolean' } }),
      limit: P.num('Items', 'Einträge', { def: 30, min: 1, max: 200, int: true }),
    },
    example: { props: { source: 'sample', title: L('Launch plan', 'Startplan'), label: 'row.title', done: 'row.done' }, data: [WORK] },
  }),

  comments: defineBlock({
    name: L('Comment thread', 'Kommentarverlauf'),
    desc: L('Messages with author and time, and a box to write a new one.', 'Nachrichten mit Autor und Zeit, und ein Feld für eine neue.'),
    family: 'platform',
    icon: 'comment',
    keywords: ['discussion', 'notes', 'chat', 'conversation', 'replies', 'feedback'],
    summary: 'A thread: one message per row (author, text, date) and a composer bound to a text state; sending calls onSend, typically a record.create of a note.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Messages', 'Nachrichten', ['records', 'static', 'insight']),
      author: row('Author', 'Autor', 'row.author'),
      text: row('Text', 'Text', 'row.body'),
      date: row('Date', 'Datum', 'row.date', 'any'),
      draft: P.state('The new message is kept in', 'Die neue Nachricht wird gespeichert in', ['text'], { opt: true }),
      onSend: P.call('When sent', 'Beim Senden', { when: { prop: 'draft', in: ['__set'] } }),
      placeholder: P.tpl('Placeholder', 'Platzhalter', { opt: true }),
      order: P.pick('Order', 'Reihenfolge', ['asc', 'desc'] as const, 'asc', { seg: true, labels: { asc: L('Oldest first', 'Älteste zuerst'), desc: L('Newest first', 'Neueste zuerst') } }),
    },
    example: { props: { source: 'sample', author: 'row.author', text: 'row.body', date: 'row.date', draft: 'draft', placeholder: L('Write a comment…', 'Kommentar schreiben …') }, data: [POSTS], state: [{ key: 'draft', type: { kind: 'text' } }] },
  }),

  people: defineBlock({
    name: L('People', 'Personen'),
    desc: L('The members of the workspace as cards or a list, with their role.', 'Die Mitglieder des Arbeitsbereichs als Karten oder Liste, mit ihrer Rolle.'),
    family: 'platform',
    icon: 'members',
    keywords: ['team', 'members', 'directory', 'colleagues', 'who is who'],
    summary: 'Lists the workspace members the viewer may see (name, role, e-mail). Reads the member directory, not a data source.',
    props: {
      layout: P.pick('Shown as', 'Angezeigt als', ['cards', 'list'] as const, 'cards', { seg: true, labels: { cards: L('Cards', 'Karten'), list: L('List', 'Liste') } }),
      search: P.bool('Search box', 'Suchfeld', true),
      limit: P.num('Show up to', 'Höchstens zeigen', { def: 12, min: 1, max: 100, int: true }),
    },
    example: { props: { layout: 'cards', limit: 6 } },
  }),

  gallery: defineBlock({
    name: L('File gallery', 'Dateigalerie'),
    desc: L('Pictures as a grid with a viewer, other files as download tiles.', 'Bilder als Raster mit Betrachter, andere Dateien als Download-Kacheln.'),
    family: 'platform',
    icon: 'gallery',
    keywords: ['files', 'attachments', 'images', 'photos', 'documents', 'lightbox'],
    summary: 'Shows the files of a file field (files = expression such as props.record.attachments) or asset ids from an upload; images open in a viewer.',
    props: {
      files: P.expr('Files', 'Dateien', { expect: 'any', def: '' }),
      columns: P.pick('Columns', 'Spalten', ['2', '3', '4', '5'] as const, '3', { seg: true }),
      empty: P.tpl('When empty', 'Wenn leer', { opt: true }),
    },
    example: { props: { files: '[]', empty: L('No files yet. Attach some to the record.', 'Noch keine Dateien. Hängen Sie welche an den Datensatz.') } },
  }),

  approval: defineBlock({
    name: L('Approval', 'Freigabe'),
    desc: L('A decision card: what is asked, by whom, until when, with Approve and Reject (and a comment).', 'Eine Entscheidungskarte: was gefragt wird, von wem, bis wann, mit Freigeben und Ablehnen (und Kommentar).'),
    family: 'platform',
    icon: 'check',
    keywords: ['approve', 'reject', 'decision', 'review', 'sign off', 'request', 'task'],
    summary: 'Shows one request (first row of source, or the row in scope) with Approve and Reject buttons calling onApprove / onReject. decided hides the buttons and shows the outcome. Wire the actions to task.complete or record.update.',
    scopes: { row: 'source' },
    props: {
      source: P.source('Request', 'Anfrage', ['record', 'records', 'static'], { opt: true }),
      title: P.tpl('Title', 'Titel', { scope: 'row', def: '{{row.title}}' }),
      text: P.tpl('Details', 'Details', { scope: 'row', opt: true, long: true }),
      requester: P.expr('Asked by', 'Angefragt von', { scope: 'row', expect: 'text', opt: true }),
      due: P.expr('Decide by', 'Entscheiden bis', { scope: 'row', expect: 'any', opt: true }),
      decided: P.expr('Already decided when', 'Bereits entschieden, wenn', { scope: 'row', expect: 'boolean', opt: true }),
      outcome: P.expr('Outcome (approved, rejected)', 'Ergebnis (approved, rejected)', { scope: 'row', expect: 'text', opt: true }),
      comment: P.state('The comment is kept in', 'Der Kommentar wird gespeichert in', ['text'], { opt: true }),
      approveLabel: P.tpl('Approve button', 'Freigeben-Schaltfläche', { def: 'Approve' }),
      rejectLabel: P.tpl('Reject button', 'Ablehnen-Schaltfläche', { def: 'Reject' }),
      onApprove: P.call('When approved', 'Bei Freigabe', { scope: 'row' }),
      onReject: P.call('When rejected', 'Bei Ablehnung', { scope: 'row' }),
    },
    example: {
      props: { source: 'sample', title: L('Leave request: {{data.sample.first.title}}', 'Urlaubsantrag: {{data.sample.first.title}}'), text: L('5 days from 19 Oct 2026', '5 Tage ab 19.10.2026'), requester: 'data.sample.first.title', due: 'addDays(today(), 3)', comment: 'note' },
      data: [PEOPLE],
      state: [{ key: 'note', type: { kind: 'text' } }],
    },
  }),

  aichat: defineBlock({
    name: L('Ask the Copilot', 'Copilot fragen'),
    desc: L('A question box with suggestions that hands the question to the Copilot, with context.', 'Ein Fragefeld mit Vorschlägen, das die Frage samt Kontext an den Copilot übergibt.'),
    family: 'platform',
    icon: 'sparkle',
    keywords: ['ai', 'assistant', 'chat', 'copilot', 'ask', 'prompt', 'help'],
    summary: 'A prompt box that opens the Copilot with the question (and the context text appended). suggestions are one-click questions.',
    props: {
      title: P.tpl('Title', 'Titel', { def: 'Ask the Copilot' }),
      placeholder: P.tpl('Placeholder', 'Platzhalter', { opt: true }),
      context: P.tpl('Context sent along', 'Mitgeschickter Kontext', { opt: true, long: true, hint: L('Added to every question, e.g. the name of the record.', 'Wird jeder Frage angehängt, z. B. der Name des Datensatzes.') }),
      suggestions: P.items('Suggestions', 'Vorschläge', { text: P.tpl('Question', 'Frage', { def: '' }) }, { titleKey: 'text', max: 6, def: [], addLabel: L('Add a suggestion', 'Vorschlag hinzufügen') }),
    },
    example: { props: { title: L('Ask the Copilot', 'Copilot fragen'), placeholder: L('What do you want to know?', 'Was möchten Sie wissen?'), suggestions: [{ text: L('Summarise this week', 'Diese Woche zusammenfassen') }, { text: L('What is overdue?', 'Was ist überfällig?') }] } },
  }),

  slotpicker: defineBlock({
    name: L('Meeting slots', 'Terminvorschläge'),
    desc: L('Free times of a meeting type to pick from, with the booking calendars of the people involved.', 'Freie Zeiten eines Terminarts zur Auswahl, unter Berücksichtigung der Kalender der Beteiligten.'),
    family: 'platform',
    icon: 'calendar',
    keywords: ['booking', 'schedule a meeting', 'availability', 'appointment', 'calendly', 'free times'],
    summary: 'Shows the free slots of a meeting type (Meetings settings). The chosen start is kept in a text state and passed to onPick (event.start, event.end); book it with an action.',
    props: {
      meetingType: P.ref('Meeting type', 'Terminart', 'meeting_type'),
      bind: P.state('The chosen time is kept in', 'Die gewählte Zeit wird gespeichert in', ['text'], { opt: true }),
      onPick: P.call('When a time is chosen', 'Wenn eine Zeit gewählt wird', { extras: { start: 'text', end: 'text' } }),
      stacked: P.bool('Calendar above times', 'Kalender über den Zeiten', false),
    },
    example: { props: { meetingType: '' } },
  }),

  pdfpreview: defineBlock({
    name: L('PDF preview', 'PDF-Vorschau'),
    desc: L('Pages of a PDF file, one at a time, with page arrows and a download.', 'Seiten einer PDF-Datei, eine nach der anderen, mit Seitenpfeilen und Download.'),
    family: 'platform',
    icon: 'file',
    keywords: ['document', 'contract', 'offer', 'pdf viewer', 'preview', 'generated pdf'],
    summary: 'Draws a PDF: file = an expression returning a file value of a file field (a generated PDF stored on a record).',
    props: {
      file: P.expr('PDF file', 'PDF-Datei', { expect: 'any', def: '' }),
      height: P.num('Height', 'Höhe', { def: 420, min: 200, max: 1000, int: true, unit: L('px', 'px') }),
      download: P.bool('Download button', 'Download-Schaltfläche', true),
    },
    example: { props: { file: '' } },
  }),

  signrequest: defineBlock({
    name: L('Signature status', 'Unterschriftsstatus'),
    desc: L('The signature requests of a record with who has signed and who is next.', 'Die Unterschriftsanfragen eines Datensatzes mit den bereits Unterzeichnenden und dem nächsten Schritt.'),
    family: 'platform',
    icon: 'pencil',
    keywords: ['esign', 'contract', 'docusign', 'signers', 'signed', 'waiting for'],
    summary: 'Lists the signature requests of a record (object + record id): status, signed/total and who is waiting. Opens the signatures page on click.',
    props: {
      object: P.object('Object', 'Objekt'),
      record: P.expr('Record', 'Datensatz', { expect: 'any', def: '' }),
      title: P.tpl('Title', 'Titel', { opt: true }),
    },
    example: { props: { object: '', record: '', title: L('Signatures', 'Unterschriften') } },
  }),
};
